import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { toast } from 'react-toastify';
import CircularProgress from '@mui/material/CircularProgress';
import './style.css';

const GoogleCallbackPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { googleLogin } = useAuth();
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(false);
    const hasProcessed = useRef(false);

    useEffect(() => {
        // Prevent double execution (React 18 Strict Mode runs effects twice)
        if (hasProcessed.current || processing) {
            return;
        }

        const handleGoogleCallback = async () => {
            // Mark as processing
            hasProcessed.current = true;
            setProcessing(true);

            // Get the authorization code from URL
            const code = searchParams.get('code');
            const errorParam = searchParams.get('error');

            console.log('GoogleCallbackPage - code:', code);
            console.log('GoogleCallbackPage - errorParam:', errorParam);
            
            // Check for errors from Google
            if (errorParam) {
                setError('Google authentication was cancelled or failed');
                toast.error('Google authentication failed');
                setTimeout(() => navigate('/signup'), 3000);
                return;
            }

            if (!code) {
                setError('No authorization code received');
                toast.error('Authentication failed');
                setTimeout(() => navigate('/signup'), 3000);
                return;
            }

            try {
                // Exchange code for tokens and create/login user
                await googleLogin(code);
                
                // Get action from localStorage (persists across tabs)
                const action = localStorage.getItem('pending_oauth_action');
                console.log('GoogleCallbackPage - Action from localStorage:', action);
                
                // Clear the action from localStorage
                localStorage.removeItem('pending_oauth_action');
                
                // Determine redirect path based on action
                let returnUrl = '/dashboard'; // default
                if (action === 'level-test') {
                    returnUrl = '/level-test';
                } else if (action === 'demo') {
                    returnUrl = '/demo-confirmation';
                } else if (action === 'subscribe') {
                    returnUrl = '/subscribe';
                }
                
                console.log('GoogleCallbackPage - Redirecting to:', returnUrl);
                
                toast.success('Successfully signed in with Google!');
                
                // Navigate to the appropriate page
                navigate(returnUrl);
            } catch (err) {
                console.error('Google OAuth error:', err);
                const errorMessage = err.response?.data?.error?.message || 'Failed to sign in with Google';
                setError(errorMessage);
                toast.error(errorMessage);
                setTimeout(() => navigate('/signup'), 3000);
            } finally {
                setProcessing(false);
            }
        };

        handleGoogleCallback();
    }, [searchParams, navigate, googleLogin, processing]);

    return (
        <div className="google-callback-page">
            <div className="callback-container">
                {error ? (
                    <div className="callback-error">
                        <div className="error-icon">❌</div>
                        <h2>Authentication Failed</h2>
                        <p>{error}</p>
                        <p className="redirect-message">Redirecting to sign up page...</p>
                    </div>
                ) : (
                    <div className="callback-loading">
                        <CircularProgress size={60} sx={{ color: '#ffb100' }} />
                        <h2>Completing Google Sign-In</h2>
                        <p>Please wait while we set up your account...</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GoogleCallbackPage;
